import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Filter, RefreshCw, Search, Send } from 'lucide-react'
import toastService from '@/services/toast/service'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { generatePaginationPages } from '@/lib/pagination'
import { TableSkeleton } from '@/components/skeletons'
import { RelativeTime } from '@/components/RelativeTime'
import { useDebounce } from '@/hooks/useDebounce'
import {
  trackingAdminService,
  PLATFORM_EVENTS,
  PLATFORM_FUNNELS,
  type AdminTrackingLog,
  type AdminPixel,
  type TrackingLogStatus,
} from '@/services/trackingAdmin'

const PER_PAGE = 25
const ALL = 'all'
const COLUMN_COUNT = 6

const funnelLabel = (v: string) => PLATFORM_FUNNELS.find((f) => f.value === v)?.label ?? v

const STATUS_META: Record<TrackingLogStatus, { label: string; tone: 'success' | 'danger' | 'warning' | 'neutral' }> = {
  success: { label: 'Success', tone: 'success' },
  client_error: { label: 'Client error', tone: 'danger' },
  server_error: { label: 'Server error', tone: 'warning' },
  timeout: { label: 'Timeout', tone: 'neutral' },
}

const pretty = (v: unknown) => {
  try {
    return JSON.stringify(v ?? null, null, 2)
  } catch {
    return String(v)
  }
}

/** The request/response payload shell used in the detail sheet. */
function JsonCard({ label, description, value }: { label: string; description: string; value: unknown }) {
  const text = pretty(value)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      toastService.success('Copied')
    } catch {
      toastService.error("Couldn't copy")
    }
  }

  return (
    <Card className="gap-0 overflow-hidden py-0 shadow-sm">
      <CardHeader className="gap-0 border-b px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">{label}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Button variant="outline" size="sm" className="text-sm" onClick={copy}>
            Copy
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <pre className="max-h-96 overflow-auto whitespace-pre p-4 font-mono text-xs leading-relaxed text-foreground/90">
          {text}
        </pre>
      </CardContent>
    </Card>
  )
}

export default function LogsCard({ pixels }: { pixels: AdminPixel[] }) {
  const [rows, setRows] = useState<AdminTrackingLog[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [pixelId, setPixelId] = useState(ALL)
  const [event, setEvent] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [funnel, setFunnel] = useState(ALL)
  const [searchQuery, setSearchQuery] = useState('')
  const [selected, setSelected] = useState<AdminTrackingLog | null>(null)

  const debouncedSearch = useDebounce(searchQuery, 300)
  const hasActiveFilters =
    pixelId !== ALL || event !== ALL || status !== ALL || funnel !== ALL || debouncedSearch !== ''

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await trackingAdminService.listLogs({
        page,
        limit: PER_PAGE,
        pixelId: pixelId === ALL ? undefined : pixelId,
        event: event === ALL ? undefined : event,
        status: status === ALL ? undefined : status,
        funnel: funnel === ALL ? undefined : funnel,
        search: debouncedSearch || undefined,
      })
      setRows(res.data)
      setTotalPages(Math.max(res.meta.totalPages, 1))
      setTotal(res.meta.total)
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't load logs")
    } finally {
      setLoading(false)
    }
  }, [page, pixelId, event, status, funnel, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  // A new query restarts from page 1, as the filter selects do below.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  // Filter changes restart from page 1.
  const withReset = (setter: (v: string) => void) => (v: string) => {
    setter(v)
    setPage(1)
  }

  const selectedMeta = selected ? STATUS_META[selected.status] ?? STATUS_META.timeout : null

  // One pixel id can sit on several rows; the filter matches on the id, so it
  // lists each id once (the first row's label).
  const pixelOptions = pixels.filter((p, i) => pixels.findIndex((q) => q.pixelId === p.pixelId) === i)

  return (
    <div className="rounded-lg border bg-card">
      {/* Toolbar: search left, filters and refresh right */}
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by event id or agency..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={pixelId} onValueChange={withReset(setPixelId)}>
            <SelectTrigger className="h-9 w-[200px] text-sm">
              <SelectValue placeholder="Pixel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All pixels</SelectItem>
              {pixelOptions.map((p) => <SelectItem key={p.pixelId} value={p.pixelId}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={event} onValueChange={withReset(setEvent)}>
            <SelectTrigger className="h-9 w-[150px] text-sm">
              <SelectValue placeholder="Event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All events</SelectItem>
              {PLATFORM_EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={funnel} onValueChange={withReset(setFunnel)}>
            <SelectTrigger className="h-9 w-[150px] text-sm">
              <SelectValue placeholder="Funnel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All funnels</SelectItem>
              {PLATFORM_FUNNELS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={withReset(setStatus)}>
            <SelectTrigger className="h-9 w-[150px] text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {(Object.keys(STATUS_META) as TrackingLogStatus[]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={load} disabled={loading} aria-label="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-center min-w-[200px]">Event</TableHead>
              <TableHead className="text-center min-w-[200px]">Pixel</TableHead>
              <TableHead className="text-center w-[130px]">Funnel</TableHead>
              <TableHead className="text-center min-w-[160px]">Agency</TableHead>
              <TableHead className="text-center w-[140px]">Sent</TableHead>
              <TableHead className="text-center w-[150px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className={loading ? 'pointer-events-none' : ''}>
            {loading && rows.length === 0 ? (
              <TableSkeleton columns={COLUMN_COUNT} rows={5} />
            ) : rows.length === 0 && hasActiveFilters ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                  <div className="flex flex-col items-center justify-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Filter className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No matching logs</h3>
                    <p className="text-sm text-muted-foreground">
                      No logs match your current search or filters.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                  <div className="flex flex-col items-center justify-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Send className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No logs yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Sends appear here as soon as a pixel fires.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const meta = STATUS_META[r.status] ?? STATUS_META.timeout
                return (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="whitespace-nowrap text-sm">{r.action}</span>
                        {r.isTest && <Badge tone="info" size="tag">Test Event</Badge>}
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="flex flex-col items-center">
                        {r.pixelLabel
                          ? <span className="font-medium text-sm">{r.pixelLabel}</span>
                          : <span className="text-muted-foreground text-sm italic">Removed pixel</span>}
                        <span className="text-xs text-muted-foreground font-mono">{r.pixelId ?? ''}</span>
                      </div>
                    </TableCell>

                    {/* Which funnel the conversion came from, not which the pixel covers. */}
                    <TableCell className="text-center">
                      {r.funnel ? (
                        <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                          {funnelLabel(r.funnel)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">-</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center">
                      {r.agency ? (
                        <Link to={`/agencies/${r.agency.publicId}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                          {r.agency.name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">{r.isTest ? 'Backoffice' : 'Unknown'}</span>
                      )}
                    </TableCell>

                    <TableCell className="whitespace-nowrap text-center text-sm text-muted-foreground">
                      <RelativeTime date={r.createdAtUtc} className="cursor-default" />
                    </TableCell>

                    {/* One pill carries the outcome and the HTTP code, as in the app's Logs tab. */}
                    <TableCell className="text-center">
                      <Badge tone={meta.tone}>
                        {meta.label}
                        {r.httpStatus ? ` · ${r.httpStatus}` : ''}
                      </Badge>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-4 border-t">
          <p className="text-sm text-muted-foreground">Showing {rows.length} of {total} logs</p>
          <Pagination className={`mx-0 w-auto ${loading ? 'pointer-events-none opacity-50' : ''}`}>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious onClick={() => setPage((p) => Math.max(1, p - 1))} className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'} />
              </PaginationItem>
              {generatePaginationPages(page, totalPages).map((item, i) =>
                item === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}><span className="px-2 text-muted-foreground">...</span></PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationLink onClick={() => setPage(item as number)} isActive={(item as number) === page} className="cursor-pointer">
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'} />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
          {selected && selectedMeta && (
            <>
              <SheetHeader className="shrink-0 gap-1 border-b px-5 py-4">
                <SheetTitle className="text-base">{selected.action}</SheetTitle>
                <SheetDescription className="text-xs">
                  {selected.pixelLabel ?? 'Removed pixel'} · {selected.pixelId ?? '-'}
                </SheetDescription>
              </SheetHeader>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-muted/30 p-5">
                {selected.error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
                    {selected.error}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Badge tone={selectedMeta.tone}>{selectedMeta.label}</Badge>
                  {selected.httpStatus != null && (
                    <span className="text-sm text-muted-foreground">HTTP {selected.httpStatus}</span>
                  )}
                  {selected.isTest && <Badge tone="info" size="tag">Test Event</Badge>}
                  <RelativeTime date={selected.createdAtUtc} className="ml-auto text-xs text-muted-foreground" />
                </div>

                <Card className="gap-0 overflow-hidden py-0 shadow-sm">
                  <CardHeader className="gap-0 border-b px-5 py-4">
                    <CardTitle className="text-base">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <dl className="divide-y text-sm">
                      <div className="flex items-center justify-between gap-4 px-5 py-2.5">
                        <dt className="text-muted-foreground">Event id</dt>
                        <dd className="font-mono text-xs text-right break-all">{selected.reference ?? '-'}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 px-5 py-2.5">
                        <dt className="text-muted-foreground">Delivery id</dt>
                        <dd className="font-mono text-xs text-right break-all">{selected.deliveryId}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 px-5 py-2.5">
                        <dt className="text-muted-foreground">Agency</dt>
                        <dd className="font-mono text-xs text-right break-all">
                          {selected.agency ? (
                            <Link to={`/agencies/${selected.agency.publicId}`} className="hover:underline">
                              {selected.agency.name}
                            </Link>
                          ) : (
                            selected.isTest ? 'Backoffice' : 'Unknown'
                          )}
                        </dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>

                <JsonCard label="Request" description="What we sent to Meta (access token masked)" value={selected.request} />
                <JsonCard label="Response" description="What Meta returned" value={selected.response} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
